import * as React from "react"
import { Head, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { ConsultationBooking, PageProps } from "@/types/inertia"
import { cn } from "@/lib/utils"

type Props = PageProps & {
  upcoming: ConsultationBooking[]
  past: ConsultationBooking[]
}

const STATUS_TONES: Record<string, "accent" | "neutral" | "signal" | "muted"> = {
  confirmed: "accent",
  completed: "neutral",
  no_show: "signal",
  cancelled: "muted",
}

const STATUS_LABELS: Record<string, string> = {
  confirmed: "Confirmed",
  completed: "Completed",
  no_show: "No-show",
  cancelled: "Cancelled",
}

function telHref(phone: string) {
  return `tel:${phone.replace(/\s/g, "")}`
}

export default function ConsultationsIndex() {
  const { props } = usePage<Props>()
  const { upcoming, past } = props
  const flash = props.flash

  const [tab, setTab] = React.useState<"upcoming" | "past">("upcoming")
  const [cancelTarget, setCancelTarget] = React.useState<ConsultationBooking | null>(null)
  const [notifyClient, setNotifyClient] = React.useState(true)

  const rows = tab === "upcoming" ? upcoming : past

  function confirmCancel() {
    if (!cancelTarget) return
    router.patch(`/admin/consultations/${cancelTarget.id}`, {
      consultation_booking: { status: "cancelled" },
      notify: notifyClient ? "1" : "0",
    })
    setCancelTarget(null)
  }

  function markStatus(booking: ConsultationBooking, status: "completed" | "no_show") {
    router.patch(`/admin/consultations/${booking.id}`, {
      consultation_booking: { status },
    })
  }

  return (
    <AdminShell>
      <Head title="Consultations">
        <meta name="description" content="Manage free 15-minute consultation bookings." />
        <meta property="og:title" content="Consultations" />
        <meta property="og:description" content="Manage free 15-minute consultation bookings." />
      </Head>

      <PageHeader
        title="Consultations"
        description="Free 15-minute consultations booked on Gisela's /book page. Times shown in UK time."
      />

      {flash?.notice && <p className="mt-4 text-sm text-accent">{flash.notice}</p>}

      <div className="mt-6 flex gap-2">
        {(
          [
            { key: "upcoming", label: `Upcoming (${upcoming.length})` },
            { key: "past", label: `Past (${past.length})` },
          ] as const
        ).map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              "rounded-lg px-4 py-2 text-sm transition-colors",
              tab === key ? "bg-surface font-medium text-ink-display" : "text-ink-muted hover:text-ink-body"
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-4 overflow-x-auto rounded-lg border border-hairline">
        <table className="w-full text-sm">
          <thead className="border-b border-hairline bg-surface">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">When (UK)</th>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">Name</th>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">Phone</th>
              <th className="hidden px-4 py-3 text-left font-medium text-ink-muted lg:table-cell">Note</th>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">Status</th>
              <th className="px-4 py-3 text-right font-medium text-ink-muted">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-ink-muted">
                  No {tab} consultations.
                </td>
              </tr>
            )}
            {rows.map((booking) => (
              <tr key={booking.id} className="bg-white transition-colors hover:bg-surface/30">
                <td className="whitespace-nowrap px-4 py-3 tabular-nums text-ink-body">
                  {booking.london_label}
                </td>
                <td className="px-4 py-3">
                  <p className="text-ink-body">{booking.name}</p>
                  <p className="text-xs text-ink-muted">{booking.email}</p>
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  <a href={telHref(booking.phone)}>{booking.phone}</a>
                  <p className="text-xs text-ink-muted">
                    {booking.contact_method === "whatsapp" ? "WhatsApp" : "Mobile call"}
                    {booking.client_timezone &&
                      booking.client_timezone !== "Europe/London" &&
                      ` · ${booking.client_timezone}`}
                  </p>
                </td>
                <td className="hidden max-w-xs px-4 py-3 text-ink-muted lg:table-cell">
                  {booking.note && (
                    <p className="truncate" title={booking.note}>
                      {booking.note}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Badge tone={STATUS_TONES[booking.status] ?? "neutral"}>
                    {STATUS_LABELS[booking.status] ?? booking.status}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {tab === "upcoming" && booking.status === "confirmed" && (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="text-danger-display hover:border-danger hover:bg-danger-faded"
                        onClick={() => {
                          setCancelTarget(booking)
                          setNotifyClient(true)
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                    {tab === "past" && booking.status === "confirmed" && (
                      <>
                        <Button variant="secondary" size="sm" onClick={() => markStatus(booking, "completed")}>
                          Completed
                        </Button>
                        <Button variant="secondary" size="sm" onClick={() => markStatus(booking, "no_show")}>
                          No-show
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={cancelTarget !== null} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel this consultation?</DialogTitle>
            <DialogDescription>
              This cancels <strong>{cancelTarget?.name}</strong>&rsquo;s call on{" "}
              <strong>{cancelTarget?.london_label}</strong> and frees the slot for other visitors.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-center gap-2 font-normal text-ink-body">
            <Checkbox checked={notifyClient} onChange={(e) => setNotifyClient(e.target.checked)} />
            <span className="text-sm">Email the client to let them know</span>
          </label>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCancelTarget(null)}>
              Keep booking
            </Button>
            <Button variant="danger" onClick={confirmCancel}>
              Cancel booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  )
}
